import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingLayout from './OnboardingLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus, Trash2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const OnboardingDrivers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    pin: ''
  });

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/onboarding/drivers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrivers(response.data.drivers || []);
    } catch (error) {
      console.error('Load drivers error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name) {
      toast.error('First and last name are required');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/onboarding/drivers`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Driver added!');
      setFormData({ first_name: '', last_name: '', email: '', phone: '', pin: '' });
      setShowForm(false);
      loadDrivers();
    } catch (error) {
      console.error('Create driver error:', error);
      if (error.response?.status === 403) {
        toast.error('Driver limit reached. Upgrade your plan to add more drivers.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to add driver');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (drivers.length === 0) {
      toast.error('Please add at least one driver before continuing');
      return;
    }
    navigate('/onboarding/cashx');
  };

  return (
    <OnboardingLayout currentStep={2}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            Add Drivers
          </h2>
          <p className="text-gray-500 mt-1">
            Create driver accounts for your team
          </p>
        </div>

        {/* Driver List */}
        {drivers.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-semibold">Added Drivers ({drivers.length})</h3>
            {drivers.map((driver) => (
              <Card key={driver.user_id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{driver.name}</p>
                    <p className="text-sm text-gray-500">
                      {driver.email || 'No email'} • {driver.phone || 'No phone'}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Add Driver Form */}
        {showForm ? (
          <Card className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email (Optional)</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone (Optional)</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="pin">PIN Code (Optional)</Label>
                <Input
                  id="pin"
                  name="pin"
                  type="password"
                  value={formData.pin}
                  onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                  placeholder="4-digit PIN"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Adding...' : 'Add Driver'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        ) : (
          <Button onClick={() => setShowForm(true)} variant="outline" className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Another Driver
          </Button>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button variant="outline" onClick={() => navigate('/onboarding/company')}>
            Back
          </Button>
          <Button onClick={handleNext} disabled={drivers.length === 0}>
            Next: CashX Integration
          </Button>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingDrivers;
